// ============================================================
// enregistrement.js — ce qui part au serveur, et ce qu'on fait de sa
// réponse. Pur : aucune dépendance à React, testé depuis
// `web/enregistrement.test.js`.
// ------------------------------------------------------------
// ── POURQUOI CE MODULE EXISTE — 6 octobre 2026 ──
//
// Philippe : « Je travaille sur le devis 062, je vais voir un prix sur un
// autre devis, je reviens à mon 062 et j'ai tout perdu. » Rien ne gardait
// la navigation interne, et une estimation — une minute et demie, payée —
// ne vivait que dans l'état de l'écran.
//
// Le devis s'enregistre désormais tout seul. Ça pose une question que
// l'enregistrement manuel n'avait pas : **que faire de la réponse quand
// le deviseur a continué de taper pendant l'envoi ?** La réponse décrit
// le devis tel qu'il était envoyé ; l'appliquer telle quelle effacerait
// la frappe des dernières secondes — exactement la perte qu'on corrige.
//
//  - rien n'a bougé pendant l'envoi : la réponse remplace tout, comme
//    l'enregistrement manuel l'a toujours fait ;
//  - quelque chose a bougé : on GARDE l'écran, et on n'en reprend que ce
//    que le serveur seul pouvait donner — l'identifiant d'une pièce
//    nouvelle (sinon le prochain envoi la créerait une seconde fois) et
//    les champs qu'on ne saisit pas (totaux, dates, nom du client).
// ============================================================

/**
 * Les champs d'en-tête que l'écran modifie et que l'enregistrement
 * envoie. UNE liste : le corps et la fusion la lisent toutes les deux.
 * `web/entete-devis.test.js` vérifie que toute clé passée à `patchDevis`
 * y figure.
 */
export const CHAMPS_ENTETE = [
  'client_id', 'project', 'client_order_ref', 'delay', 'validity_days',
  'shipping_label', 'shipping_amount', 'vat_rate', 'discount_pct',
  'notes_internal', 'notes_client',
  // L'encart matière et transport du §1.1.
  'montant_matiere_retenu', 'transport_matiere',
  'matiere_fournisseur', 'matiere_offre_ref', 'matiere_offre_date',
];

/** Champs d'AFFICHAGE qu'un geste à l'écran modifie avec un champ envoyé. */
const AFFICHAGE_LOCAL = ['client_company'];

/** Le corps du PUT. Les prix ne sont pas envoyés : le serveur les recalcule. */
export function corpsEnregistrement(devis, pieces) {
  const corps = Object.fromEntries(CHAMPS_ENTETE.map((k) => [k, devis[k]]));
  corps.client_id = devis.client_id ?? null;
  corps.pieces = pieces;
  return corps;
}

/**
 * Ce que l'écran devient quand la réponse arrive.
 *
 * @param {object} o
 * @param {{devis, pieces, cles, revision}} o.local    l'écran maintenant
 * @param {{pieces, cles, revision}}        o.envoye   l'écran au moment de l'envoi
 * @param {{devis, pieces}}                 o.reponse  ce que le serveur a enregistré
 * @returns {{devis, pieces, cles, propre: boolean}} `propre` : plus rien
 *   à envoyer. Faux quand on a tapé pendant l'envoi — il faut renvoyer.
 */
export function fusionnerReponse({ local, envoye, reponse }) {
  if (local.revision === envoye.revision) {
    return { devis: reponse.devis, pieces: reponse.pieces, cles: envoye.cles, propre: true };
  }

  // Le serveur écrit `sort_order = index` : sa réponse suit l'ordre de
  // l'envoi. La pièce envoyée en position j a reçu `reponse.pieces[j]`.
  const pieces = local.pieces.map((p, i) => {
    const j = envoye.cles.indexOf(local.cles[i]);
    if (j < 0) return p; // ajoutée pendant l'envoi : rien à reprendre
    return {
      ...p,
      id: p.id ?? reponse.pieces[j]?.id ?? null,
      // La proposition a été consommée par cet envoi : la renvoyer
      // recréerait les mêmes corrections au prochain.
      proposition_id: null,
    };
  });

  const garde = [...CHAMPS_ENTETE, ...AFFICHAGE_LOCAL];
  const devis = {
    ...reponse.devis,
    ...Object.fromEntries(garde.filter((k) => k in local.devis).map((k) => [k, local.devis[k]])),
  };
  return { devis, pieces, cles: local.cles, propre: false };
}

/** Une clé de pièce, côté écran seulement : elle ne part jamais au serveur. */
let compteur = 0;
export const nouvelleCle = () => `p${Date.now().toString(36)}-${(compteur += 1)}`;
